import { Field, Item, LinkField, Text, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import { ButtonVariant } from 'components/helpers/Button/Button';
import { IconVariant } from 'components/helpers/Icon';
import Link, { LinkVariant } from 'components/helpers/Link/Link';
import ResponsiveImages from 'components/helpers/ResponsiveImages/ResponsiveImages';
import { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { getRenderingParam } from 'lib/rendering-params';
import { RecordOfFields } from 'models/fields';

import styles from './StandardPageBanner.module.scss';
import { ResponsiveImageFields } from 'models/responsive-image';

const cx = classNames.bind(styles);

type StandardPageBannerProps = ComponentProps & {
  fields: ResponsiveImageFields & {
    bannerBody: Field<string>;
    bannerBodyExpanded?: string;
    bannerLinkDestination: LinkField;
    bannerLinkIcon: Item | null;
    bannerLinkMode: Item | null;
    bannerLinkText: Field<string>;
    bannerLinkTextExpanded?: string;
    bannerEyebrow?: Field<string>;
    bannerSubtitle: Field<string>;
    bannerSubtitleExpanded?: string;
    bannerTitle: Field<string>;
    bannerTitleExpanded?: string;
    bannerTitleHeadingLevel: Item | null;
    bannerTitleTextStyle: Item | null;
    bannerSubtitleHeadingLevel: Item | null;
    bannerSubtitleTextStyle: Item | null;
  };
};

const StandardPageBanner = (props: StandardPageBannerProps): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { className, fields, params } = props;

  if (!fields) {
    return <></>;
  }

  const contentBackgroundColor =
    getRenderingParam(params.contentBackgroundColor) || 'bg-purple-60 text-white bg-inherit-dark';
  const imageDirection = getRenderingParam(params.imageDirection) || 'left';
  const presentationMode = getRenderingParam(params.presentationMode) || '70-30';

  const linkIconField = fields.bannerLinkIcon?.fields as RecordOfFields;
  const linkIconValue = getFieldValue<IconVariant>(linkIconField, 'Value');

  const linkModeField = fields.bannerLinkMode?.fields as RecordOfFields;
  const linkModeValue = getFieldValue<ButtonVariant | LinkVariant>(linkModeField, 'Value');

  const bannerTitleHeadingLevelItemField = fields.bannerTitleHeadingLevel?.fields as RecordOfFields;
  const bannerTitleHeadingLevelValue = getFieldValue<string>(bannerTitleHeadingLevelItemField, 'Value') || 'h1';

  const bannerTitleTextStyleItemField = fields.bannerTitleTextStyle?.fields as RecordOfFields;
  const bannerTitleTextStyleValue = getFieldValue<string>(bannerTitleTextStyleItemField, 'Value') || 'font-style-xl';

  const bannerSubtitleHeadingLevelItemField = fields.bannerSubtitleHeadingLevel?.fields as RecordOfFields;
  const bannerSubtitleHeadingLevelValue = getFieldValue<string>(bannerSubtitleHeadingLevelItemField, 'Value') || 'h2';

  const bannerSubtitleTextStyleItemField = fields.bannerSubtitleTextStyle?.fields as RecordOfFields;
  const bannerSubtitleTextStyleValue =
    getFieldValue<string>(bannerSubtitleTextStyleItemField, 'Value') || 'font-style-xl';

  const imagecolumnClasses: string = cx('image-column');
  const textcolumnClasses: string = cx('text-column', contentBackgroundColor);

  const imageColumn = (
    <div className={imagecolumnClasses} key="standard-page-banner-image-column">
      <ResponsiveImages
        desktopImage={fields.desktopImage}
        mobileImage={fields.mobileImage}
        tabletImage={fields.tabletImage}
        imageType={fields.imageType}
        imageAreaBackgroundColor={fields.imageAreaBackgroundColor}
      />
    </div>
  );
  const hasWhiteText = contentBackgroundColor.includes('text-white');
  const textColumn = (
    <div className={textcolumnClasses} key="standard-page-banner-text-column">
      {fields.bannerEyebrow && (
        <Text
          className={cx({ subtitle: true, [bannerSubtitleTextStyleValue]: true })}
          field={sitecoreContext.pageEditing ? fields.bannerEyebrow : { value: fields.bannerEyebrow?.value }}
          tag={'p'}
          encode={sitecoreContext.pageEditing}
        />
      )}
      <Text
        className={cx({ title: true, 'text-white': hasWhiteText, [bannerTitleTextStyleValue]: true })}
        encode={sitecoreContext.pageEditing}
        field={
          sitecoreContext.pageEditing
            ? fields.bannerTitle
            : { value: fields.bannerTitleExpanded || fields.bannerTitle?.value }
        }
        tag={bannerTitleHeadingLevelValue}
        editable={true}
      />
      <Text
        className={cx({ subtitle: true, [bannerSubtitleTextStyleValue]: true })}
        field={
          sitecoreContext.pageEditing
            ? fields.bannerSubtitle
            : { value: fields.bannerSubtitleExpanded || fields.bannerSubtitle?.value }
        }
        tag={bannerSubtitleHeadingLevelValue}
        encode={sitecoreContext.pageEditing}
      />
      <RichTextWrapper
        className={cx('body')}
        field={
          sitecoreContext.pageEditing
            ? fields.bannerBody
            : { value: fields.bannerBodyExpanded || fields.bannerBody?.value }
        }
        tag="div"
      />
      {props.fields.bannerLinkDestination?.value.href !== '' && (
        <Link
          className={cx('link', { 'text-white': hasWhiteText })}
          field={fields.bannerLinkDestination}
          icon={linkIconValue}
          showLinkTextWithChildrenPresent={false}
          variant={linkModeValue}
        >
          <Text
            field={
              sitecoreContext.pageEditing
                ? fields.bannerLinkText
                : { value: fields.bannerLinkTextExpanded || fields.bannerLinkText?.value }
            }
            encode={sitecoreContext.pageEditing}
          />
        </Link>
      )}
    </div>
  );

  return (
    <div className={cx('banner', `mode-${presentationMode}`, 'component-container-edge', className)}>
      {imageDirection === 'left' ? [imageColumn, textColumn] : [textColumn, imageColumn]}
    </div>
  );
};

export default compose<StandardPageBannerProps>(withValidDatasourceCheck(), withStyles())(StandardPageBanner);
